import { useState, useMemo, useEffect, useRef } from 'react';
import { useMsal, type IMsalContext } from '@azure/msal-react';
import useSWR from 'swr';
import { getAccounts, getConsoleConfig } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/PageHeader';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, Check, RotateCcw } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { trackBuilder } from '@/lib/rum';

type Environment = 'production' | 'staging';

const API_HOSTS = {
  production: 'https://api.sentiment.accessacloud.com',
  staging: 'https://api-staging.sentiment.accessacloud.com',
};

declare global {
  interface Window {
    oc?: {
      renderUnloadedComponents: () => void;
    };
  }
}

type CssScopeOption = 'global' | 'instance';

interface BuilderConfig {
  label: string;
  collapsible: boolean;
  labelPosition: 'before' | 'after' | 'top' | 'bottom';
  showTooltip: boolean;
  tooltipText: string;
  dismissible: boolean;
  iconSize: string;
  buttonSize: string;
  gap: string;
  padding: string;
  fontSize: string;
  cssScope: CssScopeOption;
}

const defaultConfig: BuilderConfig = {
  label: '',
  collapsible: false,
  labelPosition: 'bottom',
  showTooltip: true,
  tooltipText: '',
  dismissible: true,
  iconSize: '20px',
  buttonSize: '32px',
  gap: '4px',
  padding: '8px',
  fontSize: '14px',
  cssScope: 'instance',
};

type RegistryEnv = 'local' | 'staging' | 'prod';

const registries: Record<RegistryEnv, string> = {
  local: 'http://localhost:3000',
  staging: 'https://mfe.test.workspace.accessacloud.com/registry',
  prod: 'https://mfe.workspace.accessacloud.com/registry',
};

interface ExampleConfig {
  name: string;
  description: string;
  config: Partial<BuilderConfig>;
}

const examples: ExampleConfig[] = [
  {
    name: 'Default',
    description: 'Thumbs up/down with dismiss button',
    config: {},
  },
  {
    name: 'With Label (Collapsible)',
    description: 'Click button to reveal thumbs',
    config: {
      label: 'Was this helpful?',
      collapsible: true,
    },
  },
  {
    name: 'Label Below (Non-Collapsible)',
    description: 'Thumbs shown immediately with centered label',
    config: {
      label: 'Rate feature',
      collapsible: false,
      showTooltip: false,
    },
  },
  {
    name: 'Larger Size',
    description: 'Scaled up using CSS custom properties',
    config: {
      iconSize: '28px',
      buttonSize: '44px',
      gap: '8px',
    },
  },
];

function generatePropsCode(config: BuilderConfig): string {
  const props: string[] = [];

  if (config.label) props.push(`label="${config.label}"`);
  // When label is set, component defaults to collapsible=true, so we need to explicitly set false
  if (config.label && !config.collapsible) props.push(`collapsible={false}`);
  if (!config.collapsible && config.label && config.labelPosition !== 'bottom') {
    props.push(`labelPosition="${config.labelPosition}"`);
  }
  if (!config.showTooltip) props.push(`showTooltip={false}`);
  if (config.tooltipText) props.push(`tooltipText="${config.tooltipText}"`);
  if (!config.dismissible) props.push(`dismissible={false}`);

  const cssProps: string[] = [];
  if (config.iconSize !== '20px') cssProps.push(`--feedback-icon-size: ${config.iconSize};`);
  if (config.buttonSize !== '32px') cssProps.push(`--feedback-button-size: ${config.buttonSize};`);
  if (config.gap !== '4px') cssProps.push(`--feedback-gap: ${config.gap};`);
  if (config.padding !== '8px') cssProps.push(`--feedback-padding: ${config.padding};`);
  if (config.fontSize !== '14px') cssProps.push(`--feedback-font-size: ${config.fontSize};`);

  if (cssProps.length > 0) {
    props.push('');
    props.push('/* CSS custom properties on parent element: */');
    props.push(...cssProps);
  }

  return props.length > 0 ? props.join('\n') : '// Using all defaults';
}

function generateOCSnippet(config: BuilderConfig, accountToken: string, environment: Environment): string {
  const registryUrl = environment === 'production'
    ? 'https://mfe.workspace.accessacloud.com/registry'
    : 'https://mfe.test.workspace.accessacloud.com/registry';
  const apiHost = API_HOSTS[environment];
  const token = accountToken || 'YOUR_ACCOUNT_TOKEN';

  // Build params as formatted lines for readability
  const paramLines: string[] = [
    `    accountToken=${token}`,
    `    &product=YOUR_PRODUCT`,
    `    &feature=YOUR_FEATURE`,
    `    &customerName=CUSTOMER_NAME`,
    `    &userId=USER_ID`,
    `    &apiHost=${apiHost}`,
    `    &theme=light`,
    `    &reDisplayAfterDays=30`,
  ];

  if (config.label) paramLines.push(`    &label=${encodeURIComponent(config.label)}`);
  if (config.label && !config.collapsible) paramLines.push(`    &collapsible=false`);
  if (!config.collapsible && config.label && config.labelPosition !== 'bottom') {
    paramLines.push(`    &labelPosition=${config.labelPosition}`);
  }
  if (!config.showTooltip) paramLines.push(`    &showTooltip=false`);
  if (config.tooltipText) paramLines.push(`    &tooltipText=${encodeURIComponent(config.tooltipText)}`);
  if (!config.dismissible) paramLines.push(`    &dismissible=false`);

  // CSS custom properties
  const cssStyles: string[] = [];
  if (config.iconSize !== '20px') cssStyles.push(`--feedback-icon-size: ${config.iconSize}`);
  if (config.buttonSize !== '32px') cssStyles.push(`--feedback-button-size: ${config.buttonSize}`);
  if (config.gap !== '4px') cssStyles.push(`--feedback-gap: ${config.gap}`);
  if (config.padding !== '8px') cssStyles.push(`--feedback-padding: ${config.padding}`);
  if (config.fontSize !== '14px') cssStyles.push(`--feedback-font-size: ${config.fontSize}`);

  const hasCssStyles = cssStyles.length > 0;

  let cssBlock = '';
  let styleAttr = '';

  if (hasCssStyles) {
    if (config.cssScope === 'global') {
      cssBlock = `<style>
  /* Applies to all feedback component instances */
  oc-component[href*="evo-feature-feedback-collector"] {
${cssStyles.map(s => '    ' + s + ';').join('\n')}
  }
</style>

`;
    } else {
      styleAttr = `\n  style="${cssStyles.join('; ')}"`;
    }
  }

  return `${cssBlock}<!-- Place where you want the feedback component -->
<oc-component
  href="
    ${registryUrl}/evo-feature-feedback-collector/1/?
${paramLines.join('\n')}
  "${styleAttr}
></oc-component>`;
}

function generateReactSnippet(config: BuilderConfig, accountToken: string, environment: Environment): string {
  const registryUrl = environment === 'production'
    ? 'https://mfe.workspace.accessacloud.com/registry'
    : 'https://mfe.test.workspace.accessacloud.com/registry';
  const apiHost = API_HOSTS[environment];
  const token = accountToken || 'YOUR_ACCOUNT_TOKEN';

  // Build the customisation params
  const customParams: string[] = [];
  if (config.label) customParams.push(`      label: '${config.label}',`);
  if (config.label && !config.collapsible) customParams.push(`      collapsible: 'false',`);
  if (!config.collapsible && config.label && config.labelPosition !== 'bottom') {
    customParams.push(`      labelPosition: '${config.labelPosition}',`);
  }
  if (!config.showTooltip) customParams.push(`      showTooltip: 'false',`);
  if (config.tooltipText) customParams.push(`      tooltipText: '${config.tooltipText}',`);
  if (!config.dismissible) customParams.push(`      dismissible: 'false',`);

  const customParamsStr = customParams.length > 0 ? '\n' + customParams.join('\n') : '';

  // Build CSS custom properties
  const cssProps: string[] = [];
  if (config.iconSize !== '20px') cssProps.push(`--feedback-icon-size: ${config.iconSize}`);
  if (config.buttonSize !== '32px') cssProps.push(`--feedback-button-size: ${config.buttonSize}`);
  if (config.gap !== '4px') cssProps.push(`--feedback-gap: ${config.gap}`);
  if (config.padding !== '8px') cssProps.push(`--feedback-padding: ${config.padding}`);
  if (config.fontSize !== '14px') cssProps.push(`--feedback-font-size: ${config.fontSize}`);

  const hasCssProps = cssProps.length > 0;

  // Generate CSS block based on scope preference
  let cssBlock = '';
  let inlineStyleCode = '';

  if (hasCssProps) {
    if (config.cssScope === 'global') {
      cssBlock = `/* Add to your global CSS - applies to all instances */
oc-component[href*="evo-feature-feedback-collector"] {
${cssProps.map(p => '  ' + p + ';').join('\n')}
}

`;
    } else {
      // Instance-specific: apply via setProperty
      inlineStyleCode = cssProps.map(p => {
        const [name, value] = p.split(': ');
        return `    ocComponent.style.setProperty('${name}', '${value}');`;
      }).join('\n');
    }
  }

  return `${cssBlock}// FeatureFeedback.tsx
import { useEffect, useRef } from 'react';

interface FeatureFeedbackProps {
  feature: string;
  product?: string;
  customerName: string;
  userId: string;
  theme?: 'light' | 'dark';
}

export function FeatureFeedback({
  feature,
  product = 'MyProduct',
  customerName,
  userId,
  theme = 'light',
}: FeatureFeedbackProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const params = new URLSearchParams({
      feature,
      product,
      accountToken: '${token}',
      apiHost: '${apiHost}',
      customerName,
      userId,
      theme,
      reDisplayAfterDays: '30',${customParamsStr}
    });

    const ocComponent = document.createElement('oc-component');
    ocComponent.setAttribute(
      'href',
      '${registryUrl}/evo-feature-feedback-collector/1/?' + params.toString()
    );
${inlineStyleCode ? '\n    // Apply CSS custom properties to this instance\n' + inlineStyleCode + '\n' : ''}
    containerRef.current.innerHTML = '';
    containerRef.current.appendChild(ocComponent);

    window.oc?.renderUnloadedComponents();

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [feature, product, customerName, userId, theme]);

  return <div ref={containerRef} />;
}

// Usage:
// <FeatureFeedback
//   feature="checkout-flow"
//   product="MyApp"
//   customerName="Acme Corp"
//   userId="user@example.com"
// />`;
}

function generateEvoSnippet(config: BuilderConfig, accountToken: string, environment: Environment): string {
  const apiHost = API_HOSTS[environment];
  const token = accountToken || 'YOUR_ACCOUNT_TOKEN';

  // Build parameters object
  const params: string[] = [
    `    feature: 'YOUR_FEATURE',`,
    `    product: 'YOUR_PRODUCT',`,
    `    accountToken: '${token}',`,
    `    apiHost: '${apiHost}',`,
    `    customerName: customerName,`,
    `    userId: userId,`,
    `    theme: theme,`,
    `    reDisplayAfterDays: '30',`,
  ];

  if (config.label) params.push(`    label: '${config.label}',`);
  if (config.label && !config.collapsible) params.push(`    collapsible: 'false',`);
  if (!config.collapsible && config.label && config.labelPosition !== 'bottom') {
    params.push(`    labelPosition: '${config.labelPosition}',`);
  }
  if (!config.showTooltip) params.push(`    showTooltip: 'false',`);
  if (config.tooltipText) params.push(`    tooltipText: '${config.tooltipText}',`);
  if (!config.dismissible) params.push(`    dismissible: 'false',`);

  // Build CSS custom properties
  const cssProps: string[] = [];
  if (config.iconSize !== '20px') cssProps.push(`--feedback-icon-size: ${config.iconSize}`);
  if (config.buttonSize !== '32px') cssProps.push(`--feedback-button-size: ${config.buttonSize}`);
  if (config.gap !== '4px') cssProps.push(`--feedback-gap: ${config.gap}`);
  if (config.padding !== '8px') cssProps.push(`--feedback-padding: ${config.padding}`);
  if (config.fontSize !== '14px') cssProps.push(`--feedback-font-size: ${config.fontSize}`);

  const hasCssProps = cssProps.length > 0;

  let cssBlock = '';
  if (hasCssProps) {
    if (config.cssScope === 'global') {
      cssBlock = `/* Add to your global CSS - applies to all instances */
oc-component[href*="evo-feature-feedback-collector"] {
${cssProps.map(p => '  ' + p + ';').join('\n')}
}

`;
    } else {
      // For instance-specific, add attributes
      params.push(`    // CSS custom properties (applied via attributes)`);
      cssProps.forEach(p => {
        const [name, value] = p.split(': ');
        params.push(`    // '${name}': '${value}',`);
      });
    }
  }

  return `${cssBlock}// FeatureFeedback.tsx - for use inside EvoMfesProvider
import { EvoMfeApp } from '@access-evo/react-app-api';

interface FeatureFeedbackProps {
  feature: string;
  product?: string;
  customerName: string;
  userId: string;
  theme?: 'light' | 'dark';
}

export function FeatureFeedback({
  feature,
  product = 'MyProduct',
  customerName,
  userId,
  theme = 'light',
}: FeatureFeedbackProps) {
  // Generate unique instance ID for this feature
  const instanceId = \`feedback-\${feature}-\${userId}\`;

  return (
    <EvoMfeApp
      instanceId={instanceId}
      name="evo-feature-feedback-collector"
      parameters={{
${params.join('\n')}
      }}
    />
  );
}

// Usage (must be inside EvoMfesProvider):
// <FeatureFeedback
//   feature="checkout-flow"
//   product="MyApp"
//   customerName="Acme Corp"
//   userId="user@example.com"
// />`;
}

function generateSvelteSnippet(config: BuilderConfig, accountToken: string, environment: Environment): string {
  const registryUrl = environment === 'production'
    ? 'https://mfe.workspace.accessacloud.com/registry'
    : 'https://mfe.test.workspace.accessacloud.com/registry';
  const apiHost = API_HOSTS[environment];
  const token = accountToken || 'YOUR_ACCOUNT_TOKEN';

  // Build the customisation params
  const customParams: string[] = [];
  if (config.label) customParams.push(`      label: '${config.label}',`);
  if (config.label && !config.collapsible) customParams.push(`      collapsible: 'false',`);
  if (!config.collapsible && config.label && config.labelPosition !== 'bottom') {
    customParams.push(`      labelPosition: '${config.labelPosition}',`);
  }
  if (!config.showTooltip) customParams.push(`      showTooltip: 'false',`);
  if (config.tooltipText) customParams.push(`      tooltipText: '${config.tooltipText}',`);
  if (!config.dismissible) customParams.push(`      dismissible: 'false',`);

  const customParamsStr = customParams.length > 0 ? '\n' + customParams.join('\n') : '';

  // Build CSS custom properties
  const cssProps: string[] = [];
  if (config.iconSize !== '20px') cssProps.push(`--feedback-icon-size: ${config.iconSize}`);
  if (config.buttonSize !== '32px') cssProps.push(`--feedback-button-size: ${config.buttonSize}`);
  if (config.gap !== '4px') cssProps.push(`--feedback-gap: ${config.gap}`);
  if (config.padding !== '8px') cssProps.push(`--feedback-padding: ${config.padding}`);
  if (config.fontSize !== '14px') cssProps.push(`--feedback-font-size: ${config.fontSize}`);

  const hasCssProps = cssProps.length > 0;

  let cssBlock = '';
  let inlineStyleCode = '';

  if (hasCssProps) {
    if (config.cssScope === 'global') {
      cssBlock = `/* Add to your global CSS - applies to all instances */
oc-component[href*="evo-feature-feedback-collector"] {
${cssProps.map(p => '  ' + p + ';').join('\n')}
}

`;
    } else {
      inlineStyleCode = cssProps.map(p => {
        const [name, value] = p.split(': ');
        return `    ocComponent.style.setProperty('${name}', '${value}');`;
      }).join('\n');
    }
  }

  return `${cssBlock}<!-- FeatureFeedback.svelte -->
<script lang="ts">
  import { onMount, onDestroy } from 'svelte';

  export let feature: string;
  export let product: string = 'MyProduct';
  export let customerName: string;
  export let userId: string;
  export let theme: 'light' | 'dark' = 'light';

  let container: HTMLDivElement;

  onMount(() => {
    const params = new URLSearchParams({
      feature,
      product,
      accountToken: '${token}',
      apiHost: '${apiHost}',
      customerName,
      userId,
      theme,
      reDisplayAfterDays: '30',${customParamsStr}
    });

    const ocComponent = document.createElement('oc-component');
    ocComponent.setAttribute(
      'href',
      '${registryUrl}/evo-feature-feedback-collector/1/?' + params.toString()
    );
${inlineStyleCode ? '\n    // Apply CSS custom properties to this instance\n' + inlineStyleCode + '\n' : ''}
    container.appendChild(ocComponent);
    window.oc?.renderUnloadedComponents();
  });

  onDestroy(() => {
    if (container) {
      container.innerHTML = '';
    }
  });
</script>

<div bind:this={container}></div>

<!-- Usage: -->
<!-- <FeatureFeedback
  feature="checkout-flow"
  product="MyApp"
  customerName="Acme Corp"
  userId="user@example.com"
/> -->`;
}

function generateAngularSnippet(config: BuilderConfig, accountToken: string, environment: Environment): string {
  const registryUrl = environment === 'production'
    ? 'https://mfe.workspace.accessacloud.com/registry'
    : 'https://mfe.test.workspace.accessacloud.com/registry';
  const apiHost = API_HOSTS[environment];
  const token = accountToken || 'YOUR_ACCOUNT_TOKEN';

  // Build the customisation params
  const customParams: string[] = [];
  if (config.label) customParams.push(`      label: '${config.label}',`);
  if (config.label && !config.collapsible) customParams.push(`      collapsible: 'false',`);
  if (!config.collapsible && config.label && config.labelPosition !== 'bottom') {
    customParams.push(`      labelPosition: '${config.labelPosition}',`);
  }
  if (!config.showTooltip) customParams.push(`      showTooltip: 'false',`);
  if (config.tooltipText) customParams.push(`      tooltipText: '${config.tooltipText}',`);
  if (!config.dismissible) customParams.push(`      dismissible: 'false',`);

  const customParamsStr = customParams.length > 0 ? '\n' + customParams.join('\n') : '';

  // Build CSS custom properties
  const cssProps: string[] = [];
  if (config.iconSize !== '20px') cssProps.push(`--feedback-icon-size: ${config.iconSize}`);
  if (config.buttonSize !== '32px') cssProps.push(`--feedback-button-size: ${config.buttonSize}`);
  if (config.gap !== '4px') cssProps.push(`--feedback-gap: ${config.gap}`);
  if (config.padding !== '8px') cssProps.push(`--feedback-padding: ${config.padding}`);
  if (config.fontSize !== '14px') cssProps.push(`--feedback-font-size: ${config.fontSize}`);

  const hasCssProps = cssProps.length > 0;

  let cssBlock = '';
  let inlineStyleCode = '';

  if (hasCssProps) {
    if (config.cssScope === 'global') {
      cssBlock = `/* Add to your global styles.css - applies to all instances */
oc-component[href*="evo-feature-feedback-collector"] {
${cssProps.map(p => '  ' + p + ';').join('\n')}
}

`;
    } else {
      inlineStyleCode = cssProps.map(p => {
        const [name, value] = p.split(': ');
        return `    this.renderer.setStyle(ocComponent, '${name}', '${value}');`;
      }).join('\n');
    }
  }

  return `${cssBlock}// feature-feedback.component.ts
import {
  Component,
  Input,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  Renderer2,
} from '@angular/core';

declare global {
  interface Window {
    oc?: { renderUnloadedComponents: () => void };
  }
}

@Component({
  selector: 'app-feature-feedback',
  standalone: true,
  template: '<div #container></div>',
})
export class FeatureFeedbackComponent implements AfterViewInit, OnDestroy {
  @Input() feature!: string;
  @Input() product = 'MyProduct';
  @Input() customerName!: string;
  @Input() userId!: string;
  @Input() theme: 'light' | 'dark' = 'light';

  @ViewChild('container') containerRef!: ElementRef<HTMLDivElement>;

  constructor(private renderer: Renderer2) {}

  ngAfterViewInit(): void {
    const params = new URLSearchParams({
      feature: this.feature,
      product: this.product,
      accountToken: '${token}',
      apiHost: '${apiHost}',
      customerName: this.customerName,
      userId: this.userId,
      theme: this.theme,
      reDisplayAfterDays: '30',${customParamsStr}
    });

    const ocComponent = document.createElement('oc-component');
    ocComponent.setAttribute(
      'href',
      '${registryUrl}/evo-feature-feedback-collector/1/?' + params.toString()
    );
${inlineStyleCode ? '\n    // Apply CSS custom properties to this instance\n' + inlineStyleCode + '\n' : ''}
    this.containerRef.nativeElement.appendChild(ocComponent);
    window.oc?.renderUnloadedComponents();
  }

  ngOnDestroy(): void {
    if (this.containerRef?.nativeElement) {
      this.containerRef.nativeElement.innerHTML = '';
    }
  }
}

// Usage in template:
// <app-feature-feedback
//   feature="checkout-flow"
//   product="MyApp"
//   customerName="Acme Corp"
//   userId="user@example.com"
// />`;
}

function CodeBlock({ code, language = 'tsx', onCopy }: { code: string; language?: string; onCopy?: () => void }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    onCopy?.();
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative">
      <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto cx-mask">
        <code className={`language-${language}`}>{code}</code>
      </pre>
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 h-8 w-8"
        onClick={handleCopy}
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  );
}

interface FeedbackConfig {
  accountToken: string;
  apiHost: string;
  registry: string;
}

function getFeedbackUrls(useStaging: boolean) {
  return {
    apiHost: useStaging
      ? 'https://api-staging.sentiment.accessacloud.com'
      : 'https://api.sentiment.accessacloud.com',
    registry: useStaging
      ? 'https://mfe.test.workspace.accessacloud.com/registry'
      : 'https://mfe.workspace.accessacloud.com/registry',
  };
}

function useFeedbackConfig(instance: IMsalContext['instance']): FeedbackConfig | null {
  const { data } = useSWR('console-config', () => getConsoleConfig(instance));

  if (!data?.feedbackAccountToken) {
    return null;
  }

  const urls = getFeedbackUrls(data.feedbackUseStaging);
  return {
    accountToken: data.feedbackAccountToken,
    ...urls,
  };
}

function FeatureFeedback({
  instance,
  feature,
  product = 'feature-feedback-console',
  customerName,
  userId,
  theme = 'light',
}: {
  instance: IMsalContext['instance'];
  feature: string;
  product?: string;
  customerName: string;
  userId: string;
  theme?: 'light' | 'dark';
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const feedbackConfig = useFeedbackConfig(instance);

  useEffect(() => {
    if (!containerRef.current || !feedbackConfig) return;

    const params = new URLSearchParams({
      feature,
      product,
      accountToken: feedbackConfig.accountToken,
      apiHost: feedbackConfig.apiHost,
      customerName,
      userId,
      theme,
      reDisplayAfterDays: '30',
      label: 'Was this helpful?',
    });

    const ocComponent = document.createElement('oc-component');
    ocComponent.setAttribute(
      'href',
      `${feedbackConfig.registry}/evo-feature-feedback-collector/1/?` + params.toString()
    );

    containerRef.current.innerHTML = '';
    containerRef.current.appendChild(ocComponent);

    window.oc?.renderUnloadedComponents();

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [feature, product, customerName, userId, theme, feedbackConfig]);

  return <div ref={containerRef} />;
}

function LivePreview({ config, registry, environment, accountToken }: { config: BuilderConfig; registry: RegistryEnv; environment: Environment; accountToken: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();

  useEffect(() => {
    if (!containerRef.current) return;

    // Determine API host: use local for local registry, otherwise use environment selection
    const apiHost = registry === 'local' ? 'http://localhost:5041' : API_HOSTS[environment];

    // Build URL params
    const params = new URLSearchParams({
      feature: 'builder-preview',
      apiHost,
      accountToken: accountToken || 'preview-mode',
      product: 'Console',
      customerName: 'Preview',
      userId: 'builder-user-' + Math.random().toString(36).substring(7),
      roleId: '',
      language: 'en-GB',
      theme: theme,
      dismissible: config.dismissible.toString(),
      reDisplayAfterDays: '0',
    });

    // Add optional params only if set
    if (config.label) params.set('label', config.label);
    // When label is set, component defaults to collapsible=true, so explicitly set false
    if (config.label && !config.collapsible) params.set('collapsible', 'false');
    if (!config.collapsible && config.label && config.labelPosition !== 'bottom') {
      params.set('labelPosition', config.labelPosition);
    }
    if (!config.showTooltip) params.set('showTooltip', 'false');
    if (config.tooltipText) params.set('tooltipText', config.tooltipText);

    const componentUrl = `${registries[registry]}/evo-feature-feedback-collector/1/?${params.toString()}`;

    const ocComponent = document.createElement('oc-component');
    ocComponent.setAttribute('href', componentUrl);

    // Apply CSS custom properties
    if (config.iconSize !== '20px') ocComponent.style.setProperty('--feedback-icon-size', config.iconSize);
    if (config.buttonSize !== '32px') ocComponent.style.setProperty('--feedback-button-size', config.buttonSize);
    if (config.gap !== '4px') ocComponent.style.setProperty('--feedback-gap', config.gap);
    if (config.padding !== '8px') ocComponent.style.setProperty('--feedback-padding', config.padding);
    if (config.fontSize !== '14px') ocComponent.style.setProperty('--feedback-font-size', config.fontSize);

    containerRef.current.innerHTML = '';
    containerRef.current.appendChild(ocComponent);

    if (window.oc?.renderUnloadedComponents) {
      window.oc.renderUnloadedComponents();
    }

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [config, theme, registry, environment, accountToken]);

  return <div ref={containerRef} />;
}

export function BuilderPage() {
  const { instance } = useMsal();
  const [config, setConfig] = useState<BuilderConfig>(defaultConfig);
  const [selectedAccountToken, setSelectedAccountToken] = useState<string>('');
  const [registry, setRegistry] = useState<RegistryEnv>(import.meta.env.DEV ? 'local' : 'prod');
  const [environment, setEnvironment] = useState<Environment>('production');
  const [showOnlyMine, setShowOnlyMine] = useState(false);

  const currentUserEmail = instance.getActiveAccount()?.username;

  const { data: accounts } = useSWR('accounts', () => getAccounts(instance));

  const filteredAccounts = useMemo(() => {
    if (!accounts) return [];
    if (showOnlyMine && currentUserEmail) {
      return accounts.filter(account => account.createdBy === currentUserEmail);
    }
    return accounts;
  }, [accounts, showOnlyMine, currentUserEmail]);

  const update = <K extends keyof BuilderConfig>(key: K, value: BuilderConfig[K]) => {
    setConfig({ ...config, [key]: value });
  };

  const propsCode = useMemo(() => generatePropsCode(config), [config]);
  const ocSnippet = useMemo(() => generateOCSnippet(config, selectedAccountToken, environment), [config, selectedAccountToken, environment]);
  const evoSnippet = useMemo(() => generateEvoSnippet(config, selectedAccountToken, environment), [config, selectedAccountToken, environment]);
  const reactSnippet = useMemo(() => generateReactSnippet(config, selectedAccountToken, environment), [config, selectedAccountToken, environment]);
  const svelteSnippet = useMemo(() => generateSvelteSnippet(config, selectedAccountToken, environment), [config, selectedAccountToken, environment]);
  const angularSnippet = useMemo(() => generateAngularSnippet(config, selectedAccountToken, environment), [config, selectedAccountToken, environment]);

  const { theme: currentTheme } = useTheme();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <PageHeader title="Component Builder" description="Configure and preview the feedback component" />
        {currentUserEmail && (
          <FeatureFeedback
            instance={instance}
            feature="component-builder"
            customerName="Access"
            userId={currentUserEmail}
            theme={currentTheme}
          />
        )}
      </div>

      {/* Account & Environment Selection */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-3">
              <Label htmlFor="account-select" className="text-sm font-medium whitespace-nowrap">
                Account:
              </Label>
              <Select
                value={selectedAccountToken}
                onValueChange={setSelectedAccountToken}
              >
                <SelectTrigger id="account-select" className="w-[250px]">
                  <SelectValue placeholder="Select an account" />
                </SelectTrigger>
                <SelectContent>
                  {filteredAccounts.map((account) => (
                    <SelectItem key={account.accountToken} value={account.accountToken}>
                      {account.name} ({account.accountToken.slice(0, 8)}...)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="show-only-mine-builder"
                checked={showOnlyMine}
                onCheckedChange={setShowOnlyMine}
              />
              <Label htmlFor="show-only-mine-builder" className="text-sm cursor-pointer">
                My accounts
              </Label>
            </div>

            <div className="flex items-center gap-4">
              <Label className="text-sm text-muted-foreground">Environment:</Label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="environment"
                    checked={environment === 'production'}
                    onChange={() => {
                      setEnvironment('production');
                      trackBuilder.environmentChanged('production');
                    }}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">Production</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="environment"
                    checked={environment === 'staging'}
                    onChange={() => {
                      setEnvironment('staging');
                      trackBuilder.environmentChanged('staging');
                    }}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">Staging</span>
                </label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Controls */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Presets */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Quick Start</Label>
              <div className="flex flex-wrap gap-2">
                {examples.map((example, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setConfig({ ...defaultConfig, ...example.config });
                      trackBuilder.presetSelected(example.name);
                    }}
                  >
                    {example.name}
                  </Button>
                ))}
              </div>
            </div>

            <Separator />

            {/* Label */}
            <div className="space-y-2">
              <Label htmlFor="label">Label</Label>
              <Input
                id="label"
                value={config.label}
                onChange={(e) => update('label', e.target.value)}
                placeholder="e.g. Was this helpful?"
              />
            </div>

            {/* Collapsible */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="collapsible">Collapsible</Label>
                <p className="text-sm text-muted-foreground">
                  Start collapsed with expand button
                </p>
              </div>
              <Switch
                id="collapsible"
                checked={config.collapsible}
                onCheckedChange={(checked) => update('collapsible', checked)}
                disabled={!config.label}
              />
            </div>

            {/* Label Position */}
            <div className="space-y-2">
              <Label htmlFor="labelPosition">
                Label Position
                <span className="text-muted-foreground text-xs ml-2">(when not collapsible)</span>
              </Label>
              <Select
                value={config.labelPosition}
                onValueChange={(value) => update('labelPosition', value as BuilderConfig['labelPosition'])}
                disabled={config.collapsible || !config.label}
              >
                <SelectTrigger id="labelPosition">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="top">Top (centered)</SelectItem>
                  <SelectItem value="bottom">Bottom (centered)</SelectItem>
                  <SelectItem value="before">Before (inline)</SelectItem>
                  <SelectItem value="after">After (inline)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Show Tooltip */}
            <div className="flex items-center justify-between">
              <Label htmlFor="showTooltip">Show Tooltip</Label>
              <Switch
                id="showTooltip"
                checked={config.showTooltip}
                onCheckedChange={(checked) => update('showTooltip', checked)}
              />
            </div>

            {/* Tooltip Text */}
            <div className="space-y-2">
              <Label htmlFor="tooltipText">Tooltip Text</Label>
              <Input
                id="tooltipText"
                value={config.tooltipText}
                onChange={(e) => update('tooltipText', e.target.value)}
                placeholder="Leave feedback (default)"
                disabled={!config.showTooltip}
              />
            </div>

            {/* Dismissible */}
            <div className="flex items-center justify-between">
              <Label htmlFor="dismissible">Dismissible</Label>
              <Switch
                id="dismissible"
                checked={config.dismissible}
                onCheckedChange={(checked) => update('dismissible', checked)}
              />
            </div>

            <Separator />

            {/* CSS Custom Properties */}
            <div>
              <Label className="text-sm font-medium mb-3 block">Sizing (CSS Custom Properties)</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="iconSize" className="text-xs text-muted-foreground">Icon Size</Label>
                  <Input
                    id="iconSize"
                    value={config.iconSize}
                    onChange={(e) => update('iconSize', e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="buttonSize" className="text-xs text-muted-foreground">Button Size</Label>
                  <Input
                    id="buttonSize"
                    value={config.buttonSize}
                    onChange={(e) => update('buttonSize', e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="gap" className="text-xs text-muted-foreground">Gap</Label>
                  <Input
                    id="gap"
                    value={config.gap}
                    onChange={(e) => update('gap', e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="padding" className="text-xs text-muted-foreground">Padding</Label>
                  <Input
                    id="padding"
                    value={config.padding}
                    onChange={(e) => update('padding', e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="fontSize" className="text-xs text-muted-foreground">Font Size</Label>
                  <Input
                    id="fontSize"
                    value={config.fontSize}
                    onChange={(e) => update('fontSize', e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
              </div>

              {/* CSS Scope Selection */}
              <div className="mt-4 space-y-2">
                <Label className="text-xs text-muted-foreground">CSS Scope (for generated code)</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="cssScope"
                      checked={config.cssScope === 'global'}
                      onChange={() => update('cssScope', 'global')}
                      className="h-4 w-4"
                    />
                    <span className="text-sm">Global (all instances)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="cssScope"
                      checked={config.cssScope === 'instance'}
                      onChange={() => update('cssScope', 'instance')}
                      className="h-4 w-4"
                    />
                    <span className="text-sm">Instance-specific</span>
                  </label>
                </div>
              </div>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfig(defaultConfig)}
              className="text-muted-foreground"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to defaults
            </Button>
          </CardContent>
        </Card>

        {/* Preview & Code */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-lg">Preview</CardTitle>
              <Select
                value={registry}
                onValueChange={(value) => setRegistry(value as RegistryEnv)}
              >
                <SelectTrigger className="w-[140px] h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {import.meta.env.DEV && <SelectItem value="local">Local (3000)</SelectItem>}
                  <SelectItem value="staging">Staging</SelectItem>
                  <SelectItem value="prod">Production</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center p-8 bg-muted/50 rounded-lg min-h-[120px]">
                <LivePreview config={config} registry={registry} environment={environment} accountToken={selectedAccountToken} />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Registry: {registries[registry]}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Generated Code</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <details className="rounded-md border border-warning-200 bg-warning-50 text-sm">
                <summary className="p-3 cursor-pointer font-medium text-warning-800">
                  ⚠️ Requires OC Client Setup
                </summary>
                <div className="px-3 pb-3 text-warning-700 space-y-3">
                  <div>
                    <p className="font-medium">Evo Apps (recommended):</p>
                    <p className="mt-1">
                      Use <code className="bg-warning-100 px-1 rounded">EvoMfeApp</code> component (must be inside <code className="bg-warning-100 px-1 rounded">EvoMfesProvider</code>).{' '}
                      <a
                        href="https://evo-docs.access-workspace.com/docs/mfes/hosting-mfes"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline font-medium hover:text-warning-900"
                      >
                        See docs →
                      </a>
                    </p>
                    <pre className="mt-1 p-2 bg-warning-100 rounded text-xs overflow-x-auto">{`import { EvoMfeApp } from '@access-evo/react-app-api'

<EvoMfeApp
  instanceId="feedback-unique-id"
  name="evo-feature-feedback-collector"
  parameters={{
    feature: 'checkout-flow',
    product: 'MyApp',
    accountToken: 'YOUR_ACCOUNT_TOKEN',
    // ... other parameters from generated code
  }}
/>`}</pre>
                  </div>
                  <div>
                    <p className="font-medium">Non-Evo Apps:</p>
                    <p className="mt-1">Add to your HTML head:</p>
                    <pre className="mt-1 p-2 bg-warning-100 rounded text-xs overflow-x-auto">{`<script type="importmap">
  {
    "imports": {
      "react": "https://esm.sh/react@19.2.0",
      "react-dom/client": "https://esm.sh/react-dom@19.2.0/client",
      "i18next": "https://esm.sh/i18next@25.5.2",
      "react-i18next": "https://esm.sh/react-i18next@15.7.3?external=react,i18next"
    }
  }
</script>
<script src="https://mfe.workspace.accessacloud.com/registry/oc-client/client.js"></script>`}</pre>
                  </div>
                </div>
              </details>
              <Tabs defaultValue="evo">
                <TabsList className="mb-4 flex-wrap h-auto gap-1">
                  <TabsTrigger value="evo">Evo</TabsTrigger>
                  <TabsTrigger value="react">React</TabsTrigger>
                  <TabsTrigger value="svelte">Svelte</TabsTrigger>
                  <TabsTrigger value="angular">Angular</TabsTrigger>
                  <TabsTrigger value="html">HTML</TabsTrigger>
                  <TabsTrigger value="props">Props</TabsTrigger>
                </TabsList>
                <TabsContent value="evo">
                  <CodeBlock code={evoSnippet} onCopy={() => trackBuilder.codeCopied('evo')} />
                </TabsContent>
                <TabsContent value="react">
                  <CodeBlock code={reactSnippet} onCopy={() => trackBuilder.codeCopied('react')} />
                </TabsContent>
                <TabsContent value="svelte">
                  <CodeBlock code={svelteSnippet} language="svelte" onCopy={() => trackBuilder.codeCopied('svelte')} />
                </TabsContent>
                <TabsContent value="angular">
                  <CodeBlock code={angularSnippet} language="typescript" onCopy={() => trackBuilder.codeCopied('angular')} />
                </TabsContent>
                <TabsContent value="html">
                  <CodeBlock code={ocSnippet} language="html" onCopy={() => trackBuilder.codeCopied('html')} />
                </TabsContent>
                <TabsContent value="props">
                  <CodeBlock code={propsCode} onCopy={() => trackBuilder.codeCopied('props')} />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
